import React, { useCallback, useEffect, useState } from "react";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import MouseOutlinedIcon from "@mui/icons-material/MouseOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ToggleOffOutlinedIcon from "@mui/icons-material/ToggleOffOutlined";
import {
    Box,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Skeleton,
    Stack,
    Switch,
    TextField,
    Tooltip,
} from "@mui/material";

import { useNavigate, useParams } from "react-router-dom";

import { getDestinationIcon, getSourceIcon } from "helpers/leads/icon";

import StandardText from "components/atoms/StandardText";

import PageContainer from "../../components/atoms/PageContainer";
import { routes } from "../../Constant/routes";
import { useDebounce } from "../../hooks/useDebounce";
import LeadRetryButton from "../LeadConnectionReportsPage/LeadHistoryPage/components/LeadRetryButton";
import {
    formatShortId,
    formatTime,
    getDurationSeconds,
    getStatusCode,
    getStatusColor,
    getStatusLabel,
} from "./LeadDebugPage.helpers";
import * as Styled from "./LeadDebugPage.styled";
import type {
    ConnectionDetailData,
    DebugLog,
    LeadDebugPageProps,
} from "./LeadDebugPage.types";

function connectionMatchesRoute(
    detail: ConnectionDetailData | null | undefined,
    connectionId: string | undefined,
): detail is ConnectionDetailData {
    return (
        connectionId != null &&
        detail?.connection_detail?.id != null &&
        String(detail.connection_detail.id) === String(connectionId)
    );
}

const STATUS_OPTIONS = [
    { value: "all", label: "All Events" },
    { value: "RECEIVED", label: "Received" },
    { value: "PROCESSED", label: "Processed" },
    { value: "SENT", label: "Sent" },
    { value: "PROCESS_FAILED", label: "Process Failed" },
    { value: "DUPLICATED", label: "Duplicated" },
    { value: "FAILED_TO_FETCH_DATA", label: "Failed to Fetch Data" },
    { value: "SEND_FAILED", label: "Send Failed" },
];

function copyJson(obj: unknown): void {
    const text =
        typeof obj === "object" && obj !== null
            ? JSON.stringify(obj, null, 2)
            : String(obj);
    navigator.clipboard.writeText(text).catch(() => {});
}

function syntaxHighlightJson(json: unknown): string {
    const jsonString = JSON.stringify(json, null, 2);
    return jsonString.replace(
        /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
        (match) => {
            let cls = "json-number";
            if (/^"/.test(match)) {
                if (/:$/.test(match)) {
                    cls = "json-key";
                } else {
                    cls = "json-string";
                }
            } else if (/true|false/.test(match)) {
                cls = "json-boolean";
            } else if (/null/.test(match)) {
                cls = "json-null";
            }
            return `<span class="${cls}">${match}</span>`;
        },
    );
}

function LeadDebugPageLayout({
    connectionDetail,
    connectionLoading,
    singleConnection,
    debugLogs,
    logsLoading,
    pagination,
    selectedLogId,
    setDebugModeLoading,
    isRetryLoading,
    retryingStatId,
    dispatchGetConnectionDetail,
    dispatchGetDebugModeResults,
    dispatchSetDebugMode,
    dispatchClearLeadDebug,
    dispatchHydrateConnectionDetail,
    dispatchSetSelectedLogId,
    dispatchRetryLeadHistory,
}: LeadDebugPageProps) {
    const { id: connectionId } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [leadgenSearch, setLeadgenSearch] = useState("");
    const [appliedLeadgenSearch, setAppliedLeadgenSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [page, setPage] = useState(1);
    const [debugExpiryTime, setDebugExpiryTime] = useState<Date | null>(null);
    const [copiedIncoming, setCopiedIncoming] = useState(false);
    const [copiedOutgoing, setCopiedOutgoing] = useState(false);
    const [copiedResponse, setCopiedResponse] = useState(false);

    // Debounce search input - wait 500ms after user stops typing
    const debouncedSearch = useDebounce(leadgenSearch, 500);

    const matchingSingle = connectionMatchesRoute(
        singleConnection,
        connectionId,
    )
        ? singleConnection
        : null;
    const matchingFetched = connectionMatchesRoute(
        connectionDetail,
        connectionId,
    )
        ? connectionDetail
        : null;

    const resolvedDetail = matchingFetched ?? matchingSingle;
    const debugModeOn = resolvedDetail?.connection_detail?.debug_mode === 1;

    const selectedLog =
        selectedLogId != null
            ? (debugLogs.find((log) => log.id === selectedLogId) ?? null)
            : null;

    useEffect(() => {
        if (!connectionId) {
            navigate(routes.connection);
            return;
        }
        return () => {
            dispatchClearLeadDebug();
        };
    }, [connectionId, navigate, dispatchClearLeadDebug]);

    useEffect(() => {
        if (!connectionId || matchingFetched) return;
        if (matchingSingle) {
            dispatchHydrateConnectionDetail(matchingSingle);
            return;
        }
        dispatchGetConnectionDetail(connectionId);
    }, [
        connectionId,
        matchingSingle,
        matchingFetched,
        dispatchGetConnectionDetail,
        dispatchHydrateConnectionDetail,
    ]);

    useEffect(() => {
        setAppliedLeadgenSearch(debouncedSearch);
        setPage(1);
    }, [debouncedSearch]);

    useEffect(() => {
        // Wait until the debug-mode toggle request finishes so we don't race
        // the backend (UI is already optimistic).
        if (!connectionId || !debugModeOn || setDebugModeLoading) return;
        dispatchGetDebugModeResults({
            connectionId,
            params: {
                page,
                per_page: 10,
                leadgen_id: appliedLeadgenSearch.trim() || undefined,
                status: statusFilter === "all" ? undefined : statusFilter,
                order_by: "id",
                order_direction: "desc",
            },
        });
    }, [
        connectionId,
        debugModeOn,
        setDebugModeLoading,
        page,
        appliedLeadgenSearch,
        statusFilter,
        dispatchGetDebugModeResults,
    ]);

    const handleToggleDebug = useCallback(() => {
        setLeadgenSearch("");
        setAppliedLeadgenSearch("");
        setStatusFilter("all");
        setPage(1);
        if (!connectionId || setDebugModeLoading) return;
        const next = debugModeOn ? 0 : 1;
        if (next === 1) {
            const expiry = new Date();
            expiry.setMinutes(expiry.getMinutes() + 15);
            setDebugExpiryTime(expiry);
        } else {
            setDebugExpiryTime(null);
        }
        dispatchSetDebugMode({ connectionId, debug_mode: next as 0 | 1 });
    }, [connectionId, debugModeOn, setDebugModeLoading, dispatchSetDebugMode]);

    const handleRefreshLogs = useCallback(() => {
        if (!connectionId || !debugModeOn || setDebugModeLoading) return;
        dispatchGetDebugModeResults({
            connectionId,
            params: {
                page,
                per_page: 10,
                leadgen_id: appliedLeadgenSearch.trim() || undefined,
                status: statusFilter === "all" ? undefined : statusFilter,
                order_by: "id",
                order_direction: "desc",
            },
        });
    }, [
        connectionId,
        debugModeOn,
        setDebugModeLoading,
        page,
        appliedLeadgenSearch,
        statusFilter,
        dispatchGetDebugModeResults,
    ]);

    const handleRetrySelectedLog = useCallback(async () => {
        if (
            !connectionId ||
            !debugModeOn ||
            !selectedLog?.retry_available ||
            selectedLog.scheduled ||
            selectedLog.stat_id == null
        ) {
            return;
        }
        const result = await dispatchRetryLeadHistory({
            connectionId,
            stat_ids: [selectedLog.stat_id],
            source: "debug",
        });
        if (result?.meta?.requestStatus === "fulfilled") {
            handleRefreshLogs();
        }
    }, [
        connectionId,
        debugModeOn,
        selectedLog,
        dispatchRetryLeadHistory,
        handleRefreshLogs,
    ]);

    // Update countdown timer
    useEffect(() => {
        if (!debugModeOn || !debugExpiryTime) {
            return;
        }

        const updateTimer = () => {
            const now = new Date();
            const diff = debugExpiryTime.getTime() - now.getTime();

            if (diff <= 0) {
                return;
            }
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000);

        return () => clearInterval(interval);
    }, [debugModeOn, debugExpiryTime]);

    useEffect(() => {
        if (debugModeOn && !debugExpiryTime) {
            const expiry = new Date();
            expiry.setMinutes(expiry.getMinutes() + 15);
            setDebugExpiryTime(expiry);
        }
    }, [debugModeOn, debugExpiryTime]);

    const handleCopyIncoming = useCallback(() => {
        if (selectedLog) {
            copyJson(selectedLog.incoming_request ?? selectedLog.incoming_data);
            setCopiedIncoming(true);
            setTimeout(() => {
                setCopiedIncoming(false);
            }, 2000);
        }
    }, [selectedLog]);

    const handleCopyOutgoing = useCallback(() => {
        if (selectedLog) {
            copyJson(selectedLog.outgoing_request);
            setCopiedOutgoing(true);
            setTimeout(() => {
                setCopiedOutgoing(false);
            }, 2000);
        }
    }, [selectedLog]);

    const handleCopyResponse = useCallback(() => {
        if (selectedLog) {
            copyJson(selectedLog.outgoing_response);
            setCopiedResponse(true);
            setTimeout(() => {
                setCopiedResponse(false);
            }, 2000);
        }
    }, [selectedLog]);

    const sourceLabel =
        resolvedDetail?.source?.name ??
        resolvedDetail?.source?.details?.name ??
        "Source";
    const destinationLabel =
        resolvedDetail?.destination?.name ??
        resolvedDetail?.destination?.type ??
        "Destination";
    const sourceIcon = getSourceIcon(
        resolvedDetail?.source?.source_type_code ??
            resolvedDetail?.source?.source_type ??
            "",
    );
    const destinationIcon = getDestinationIcon(
        resolvedDetail?.destination?.type ?? "",
    );

    const headerLoading = connectionLoading || !resolvedDetail;

    const renderEndpoint = (icon: string, label: string) => (
        <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{
                fontSize: 13,
                fontWeight: 500,
                color: "text.primary",
                letterSpacing: "0.02em",
            }}
        >
            {headerLoading ? (
                <>
                    <Skeleton variant="circular" width={20} height={20} />
                    <Skeleton variant="text" width={90} sx={{ fontSize: 13 }} />
                </>
            ) : (
                <>
                    {icon && (
                        <Box
                            component="img"
                            src={icon}
                            alt=""
                            sx={{ width: 20, height: 20, display: "block" }}
                        />
                    )}
                    <Box component="span">{label}</Box>
                </>
            )}
        </Stack>
    );

    return (
        <PageContainer title="Monitor Leads" showBackButton={true}>
            <Styled.HeaderRow
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
                <Styled.SourceDestinationRow direction="row" spacing={1}>
                    {renderEndpoint(sourceIcon, sourceLabel)}
                    <ArrowForwardIcon
                        sx={{ fontSize: 18, color: "text.disabled" }}
                    />
                    {renderEndpoint(destinationIcon, destinationLabel)}
                </Styled.SourceDestinationRow>
                <Stack direction="row" alignItems="center" spacing={2}>
                    {debugModeOn && (
                        <IconButton
                            onClick={handleRefreshLogs}
                            disabled={logsLoading}
                            size="small"
                            color="primary"
                            aria-label="Refresh debug logs"
                            title="Refresh debug logs"
                        >
                            <RefreshIcon
                                sx={{
                                    animation: logsLoading
                                        ? "spin 0.8s linear infinite"
                                        : "none",
                                    "@keyframes spin": {
                                        "0%": { transform: "rotate(0deg)" },
                                        "100%": {
                                            transform: "rotate(360deg)",
                                        },
                                    },
                                }}
                            />
                        </IconButton>
                    )}
                    <Styled.DebugToggleContainer>
                        <Styled.DebugToggleLabel>
                            <Styled.DebugToggleTitle>
                                Debug Active
                            </Styled.DebugToggleTitle>
                        </Styled.DebugToggleLabel>
                        <Switch
                            checked={debugModeOn}
                            onChange={handleToggleDebug}
                            size="medium"
                        />
                    </Styled.DebugToggleContainer>
                </Stack>
            </Styled.HeaderRow>

            <Styled.MainContent>
                <Styled.Sidebar>
                    <Styled.SidebarHeader>
                        <Styled.SidebarTitle>Transactions</Styled.SidebarTitle>
                        <Styled.SidebarSearch>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search by leadgen ID..."
                                value={leadgenSearch}
                                onChange={(e) =>
                                    setLeadgenSearch(e.target.value)
                                }
                                InputProps={{
                                    startAdornment: (
                                        <SearchIcon
                                            sx={{
                                                mr: 1,
                                                color: "action.active",
                                                fontSize: 20,
                                            }}
                                        />
                                    ),
                                }}
                                variant="outlined"
                            />
                        </Styled.SidebarSearch>
                        <FormControl fullWidth size="small" variant="outlined">
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={statusFilter}
                                onChange={(e) => {
                                    setStatusFilter(e.target.value);
                                    setPage(1);
                                }}
                                label="Status"
                            >
                                {STATUS_OPTIONS.map((opt) => (
                                    <MenuItem
                                        key={opt.value || "all"}
                                        value={opt.value}
                                    >
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Styled.SidebarHeader>
                    <Styled.LogList>
                        {!debugModeOn ? (
                            <Styled.EmptyState>
                                <ToggleOffOutlinedIcon
                                    sx={{
                                        fontSize: 48,
                                        color: "rgba(0,0,0,0.2)",
                                        mb: 1.5,
                                    }}
                                />
                                <Styled.EmptyStateTitle>
                                    Debug Mode is Off
                                </Styled.EmptyStateTitle>
                                <Styled.EmptyStateDescription>
                                    Enable the toggle above to start capturing
                                    live transaction logs for this connection.
                                </Styled.EmptyStateDescription>
                            </Styled.EmptyState>
                        ) : logsLoading || setDebugModeLoading ? (
                            <>
                                {Array.from({ length: 4 }).map((_, index) => (
                                    <Styled.LogCardSkeleton key={index}>
                                        <Styled.LogCardTop
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="flex-start"
                                        >
                                            <Skeleton
                                                variant="text"
                                                width={90}
                                                sx={{ fontSize: 14 }}
                                            />
                                            <Skeleton
                                                variant="text"
                                                width={64}
                                                sx={{ fontSize: 12 }}
                                            />
                                        </Styled.LogCardTop>
                                        <Styled.LogCardMeta
                                            direction="row"
                                            justifyContent="space-between"
                                        >
                                            <Skeleton
                                                variant="text"
                                                width={130}
                                                sx={{ fontSize: 12 }}
                                            />
                                            <Skeleton
                                                variant="text"
                                                width={56}
                                                sx={{ fontSize: 12 }}
                                            />
                                        </Styled.LogCardMeta>
                                    </Styled.LogCardSkeleton>
                                ))}
                            </>
                        ) : debugLogs.length === 0 ? (
                            <Styled.EmptyState>
                                <HourglassEmptyOutlinedIcon
                                    sx={{
                                        fontSize: 48,
                                        color: "rgba(0,0,0,0.2)",
                                        mb: 1.5,
                                    }}
                                />
                                <Styled.EmptyStateTitle>
                                    No Transactions Yet
                                </Styled.EmptyStateTitle>
                                <Styled.EmptyStateDescription>
                                    Send a test lead through your connection and
                                    it will appear here.
                                </Styled.EmptyStateDescription>
                            </Styled.EmptyState>
                        ) : (
                            <>
                                {debugLogs.map((log: DebugLog) => (
                                    <Styled.LogCard
                                        key={log.id}
                                        $selected={selectedLogId === log.id}
                                        onClick={() =>
                                            dispatchSetSelectedLogId(log.id)
                                        }
                                    >
                                        <Styled.LogCardTop
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="flex-start"
                                        >
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing={1}
                                            >
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        fontSize: 14,
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    Lead #{log.id}
                                                </Box>
                                            </Stack>
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                alignItems="center"
                                                sx={{
                                                    fontSize: 12,
                                                    fontWeight: 500,
                                                    letterSpacing: "0.02em",
                                                }}
                                            >
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        color: (theme) => {
                                                            const c =
                                                                getStatusColor(
                                                                    log.status,
                                                                );
                                                            return c ===
                                                                "success"
                                                                ? theme.palette
                                                                      .success
                                                                      .main
                                                                : c === "error"
                                                                  ? theme
                                                                        .palette
                                                                        .error
                                                                        .main
                                                                  : c ===
                                                                      "primary"
                                                                    ? theme
                                                                          .palette
                                                                          .warning
                                                                          .main
                                                                    : theme
                                                                          .palette
                                                                          .text
                                                                          .secondary;
                                                        },
                                                    }}
                                                >
                                                    {getStatusLabel(log.status)}
                                                </Box>
                                                {getStatusCode(log) != null && (
                                                    <>
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                color: "text.disabled",
                                                                fontWeight: 400,
                                                            }}
                                                        >
                                                            ·
                                                        </Box>
                                                        {/**TODO: Handle all status codes    */}
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                color:
                                                                    getStatusCode(
                                                                        log,
                                                                    ) < 300
                                                                        ? "success.main"
                                                                        : "error.main",
                                                                fontVariantNumeric:
                                                                    "tabular-nums",
                                                            }}
                                                        >
                                                            {getStatusCode(log)}
                                                        </Box>
                                                    </>
                                                )}
                                            </Stack>
                                        </Styled.LogCardTop>
                                        <Styled.LogCardMeta
                                            direction="row"
                                            justifyContent="space-between"
                                        >
                                            <span>
                                                Leadgen ID:{" "}
                                                {formatShortId(log.leadgen_id)}
                                            </span>
                                            {log.sent_at ? (
                                                <span>
                                                    {formatTime(log.sent_at)}
                                                </span>
                                            ) : (
                                                <span>
                                                    {formatTime(
                                                        log.received_at,
                                                    )}
                                                </span>
                                            )}
                                        </Styled.LogCardMeta>
                                    </Styled.LogCard>
                                ))}
                            </>
                        )}
                    </Styled.LogList>
                    {debugModeOn && pagination.last_page > 1 && (
                        <Styled.SidebarFooter>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1,
                                }}
                            >
                                <IconButton
                                    size="small"
                                    onClick={() => setPage(page - 1)}
                                    disabled={page === 1}
                                    sx={{
                                        border: "1px solid rgba(0, 0, 0, 0.12)",
                                    }}
                                >
                                    <ChevronLeftIcon fontSize="small" />
                                </IconButton>
                                <Box
                                    sx={{
                                        fontSize: "14px",
                                        fontWeight: 500,
                                        color: "rgba(0, 0, 0, 0.87)",
                                        minWidth: "80px",
                                        textAlign: "center",
                                    }}
                                >
                                    Page {page} of {pagination.last_page}
                                </Box>
                                <IconButton
                                    size="small"
                                    onClick={() => setPage(page + 1)}
                                    disabled={page === pagination.last_page}
                                    sx={{
                                        border: "1px solid rgba(0, 0, 0, 0.12)",
                                    }}
                                >
                                    <ChevronRightIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Styled.SidebarFooter>
                    )}
                </Styled.Sidebar>

                <Styled.DetailSection>
                    {selectedLog ? (
                        <>
                            <Styled.DetailHeader>
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="flex-start"
                                    flexWrap="wrap"
                                    gap={2}
                                >
                                    <Box>
                                        <Styled.DetailTitle>
                                            Transaction Details
                                        </Styled.DetailTitle>
                                        <Styled.DetailMeta
                                            direction="row"
                                            spacing={2}
                                            alignItems="center"
                                            flexWrap="wrap"
                                        >
                                            <span>ID: {selectedLog.id}</span>
                                            <div className="flex items-center gap-2">
                                                Leadgen ID:{" "}
                                                <StandardText
                                                    text={
                                                        selectedLog.leadgen_id
                                                    }
                                                    copyable
                                                />
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <span>•</span>
                                                <span>
                                                    {getDurationSeconds(
                                                        selectedLog,
                                                    )}
                                                    seconds
                                                </span>
                                            </div>
                                            {selectedLog.outgoing_response !=
                                                null && (
                                                <div className="flex items-center gap-2">
                                                    <span>•</span>
                                                    <Tooltip
                                                        title={
                                                            <Styled.ResponseTooltipCard>
                                                                <Styled.JsonPanelHeader
                                                                    direction="row"
                                                                    justifyContent="space-between"
                                                                    alignItems="center"
                                                                >
                                                                    <Styled.JsonPanelTitle>
                                                                        Response
                                                                    </Styled.JsonPanelTitle>
                                                                    <Styled.CopyButton
                                                                        onClick={
                                                                            handleCopyResponse
                                                                        }
                                                                        style={{
                                                                            pointerEvents:
                                                                                copiedResponse
                                                                                    ? "none"
                                                                                    : "auto",
                                                                            opacity:
                                                                                copiedResponse
                                                                                    ? 0.6
                                                                                    : 1,
                                                                        }}
                                                                    >
                                                                        {copiedResponse
                                                                            ? "Copied to clipboard"
                                                                            : "Copy JSON"}
                                                                    </Styled.CopyButton>
                                                                </Styled.JsonPanelHeader>
                                                                <Styled.ResponseTooltipScroll>
                                                                    <Styled.JsonPre
                                                                        dangerouslySetInnerHTML={{
                                                                            __html: syntaxHighlightJson(
                                                                                selectedLog.outgoing_response,
                                                                            ),
                                                                        }}
                                                                    />
                                                                </Styled.ResponseTooltipScroll>
                                                            </Styled.ResponseTooltipCard>
                                                        }
                                                        placement="bottom-start"
                                                        enterDelay={200}
                                                        // Long enough to move the
                                                        // pointer onto the tooltip
                                                        // to scroll or copy.
                                                        leaveDelay={200}
                                                        slotProps={{
                                                            popper: {
                                                                sx: {
                                                                    "& .MuiTooltip-tooltip":
                                                                        {
                                                                            maxWidth:
                                                                                "none",
                                                                            backgroundColor:
                                                                                "transparent",
                                                                            border: 0,
                                                                            borderRadius:
                                                                                "10px",
                                                                            padding:
                                                                                "0",
                                                                        },
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                cursor: "help",
                                                                textDecoration:
                                                                    "underline",
                                                                textDecorationStyle:
                                                                    "dotted",
                                                                color:
                                                                    getStatusCode(
                                                                        selectedLog,
                                                                    ) < 300
                                                                        ? "success.main"
                                                                        : "error.main",
                                                                fontSize:
                                                                    "inherit",
                                                            }}
                                                        >
                                                            Show Response{" "}
                                                            {getStatusCode(
                                                                selectedLog,
                                                            )}
                                                        </Box>
                                                    </Tooltip>
                                                </div>
                                            )}
                                        </Styled.DetailMeta>
                                    </Box>
                                    {debugModeOn &&
                                        selectedLog.stat_id != null &&
                                        (selectedLog.retry_available ||
                                            selectedLog.scheduled) && (
                                            <LeadRetryButton
                                                scheduled={
                                                    selectedLog.scheduled
                                                }
                                                loading={
                                                    isRetryLoading &&
                                                    retryingStatId ===
                                                        selectedLog.stat_id
                                                }
                                                onClick={handleRetrySelectedLog}
                                            />
                                        )}
                                </Stack>
                            </Styled.DetailHeader>
                            <Styled.PanelsRow direction="row">
                                <Styled.JsonPanel>
                                    <Styled.JsonPanelHeader
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                    >
                                        <Styled.JsonPanelTitle>
                                            Incoming Payload
                                        </Styled.JsonPanelTitle>
                                        <Styled.CopyButton
                                            onClick={handleCopyIncoming}
                                            style={{
                                                pointerEvents: copiedIncoming
                                                    ? "none"
                                                    : "auto",
                                                opacity: copiedIncoming
                                                    ? 0.6
                                                    : 1,
                                            }}
                                        >
                                            {copiedIncoming
                                                ? "Copied to clipboard"
                                                : "Copy JSON"}
                                        </Styled.CopyButton>
                                    </Styled.JsonPanelHeader>
                                    <Styled.JsonPanelContent>
                                        <Styled.JsonPre
                                            dangerouslySetInnerHTML={{
                                                __html: syntaxHighlightJson(
                                                    selectedLog.incoming_request ??
                                                        selectedLog.incoming_data ??
                                                        {},
                                                ),
                                            }}
                                        />
                                    </Styled.JsonPanelContent>
                                </Styled.JsonPanel>

                                <Styled.ArrowDivider>
                                    <ArrowForwardIcon fontSize="small" />
                                </Styled.ArrowDivider>

                                <Styled.JsonPanel>
                                    <Styled.JsonPanelHeader
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                    >
                                        <Styled.JsonPanelTitle>
                                            Outgoing Request
                                        </Styled.JsonPanelTitle>
                                        <Styled.CopyButton
                                            onClick={handleCopyOutgoing}
                                            style={{
                                                pointerEvents: copiedOutgoing
                                                    ? "none"
                                                    : "auto",
                                                opacity: copiedOutgoing
                                                    ? 0.6
                                                    : 1,
                                            }}
                                        >
                                            {copiedOutgoing
                                                ? "Copied to clipboard"
                                                : "Copy JSON"}
                                        </Styled.CopyButton>
                                    </Styled.JsonPanelHeader>
                                    <Styled.JsonPanelContent>
                                        <Styled.JsonPre
                                            dangerouslySetInnerHTML={{
                                                __html: syntaxHighlightJson(
                                                    selectedLog.outgoing_request ??
                                                        {},
                                                ),
                                            }}
                                        />
                                    </Styled.JsonPanelContent>
                                </Styled.JsonPanel>
                            </Styled.PanelsRow>
                        </>
                    ) : (
                        <Styled.EmptyState>
                            <MouseOutlinedIcon
                                sx={{
                                    fontSize: 48,
                                    color: "rgba(0,0,0,0.2)",
                                    mb: 1.5,
                                }}
                            />
                            <Styled.EmptyStateTitle>
                                No Transaction Selected
                            </Styled.EmptyStateTitle>
                            <Styled.EmptyStateDescription>
                                Pick a transaction from the list on the left to
                                inspect its incoming and outgoing payloads.
                            </Styled.EmptyStateDescription>
                        </Styled.EmptyState>
                    )}
                </Styled.DetailSection>
            </Styled.MainContent>
        </PageContainer>
    );
}

export default LeadDebugPageLayout;
